import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import EpisodeMultiSelect, { EpisodeOption } from './EpisodeMultiSelect';

interface EpisodeEditModalProps {
  open: boolean;
  title?: string;
  availableEpisodes: readonly EpisodeOption[];
  initialSelected: readonly number[];
  onClose: () => void;
  // onSave must return a promise — modal shows loading + closes on success.
  // If it rejects, the modal stays open so the user can retry.
  onSave: (episodes: number[]) => Promise<void>;
}

const EpisodeEditModal: React.FC<EpisodeEditModalProps> = ({
  open,
  title: titleProp,
  availableEpisodes,
  initialSelected,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation('common');
  const title = titleProp ?? t('episodeEditModal.defaultTitle');
  const [draft, setDraft] = useState<number[]>([...initialSelected]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset draft whenever the modal opens with a new initial selection.
  useEffect(() => {
    if (open) {
      setDraft([...initialSelected].sort((a, b) => a - b));
      setErr(null);
      setSaving(false);
    }
  }, [open, initialSelected]);

  if (!open) return null;

  const sortedInitial = [...initialSelected].sort((a, b) => a - b);
  const sortedDraft = [...draft].sort((a, b) => a - b);
  const dirty =
    sortedInitial.length !== sortedDraft.length ||
    sortedInitial.some((v, i) => v !== sortedDraft[i]);

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      await onSave(sortedDraft);
      onClose();
    } catch (e: any) {
      setErr(e?.message || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      onClick={() => {
        if (!saving) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: '1rem 1.25rem',
          minWidth: 360,
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '1rem', fontWeight: 600 }}>{title}</div>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'none',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '1.2rem',
              color: '#666',
              lineHeight: 1,
              padding: 0,
            }}
            aria-label={t('closeAriaLabel')}
          >
            ×
          </button>
        </div>

        <div style={{ overflowY: 'auto' }}>
          <EpisodeMultiSelect
            episodes={availableEpisodes}
            selected={draft}
            onChange={setDraft}
            disabled={saving}
          />
        </div>

        {err && (
          <div style={{ color: '#d4380d', fontSize: 12 }}>{err}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              background: '#fff',
              color: '#333',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              border: '1px solid #1677ff',
              borderRadius: 4,
              background: saving || !dirty ? '#a0c4ff' : '#1677ff',
              color: '#fff',
              cursor: saving || !dirty ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? t('saving') : t('save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EpisodeEditModal;
